import { RoadWriter } from "./road-authoring.js";

const DESCRIPTIONS = [
  "The first final trial combines heat beams, void gaps and meteor pressure beyond Crab Nebula difficulty.",
  "Pass alternating beam gates while unstable rock removes the safest line.",
  "Survive sustained meteor weather across narrow split routes and collapsing landings.",
  "Cross single-lane pyroclast paths while scans and plasma arcs close behind you.",
  "Climb an exposed molten spine through chained jumps, ghost walls and elevation changes.",
  "Drop through the magma maw where ice, geysers and beams fight for control of the ship.",
  "Cross an obsidian void using three-row jumps, crumbling islands and almost no recovery road.",
  "Every world hazard enters the forge in dense, deliberately sequenced combinations.",
  "An endurance crown of precision gates, long gaps and repeated mixed-hazard examinations.",
  "The ultimate Orbitrush road: every mechanic, maximum precision and no unearned recovery.",
];

const HAZARD_CYCLE = ["beam", "meteor", "scan", "ghost", "arc", "geyser"];

function gapFor(roadIndex, stage) {
  return Math.min(3, 1 + Math.floor((roadIndex + stage + 2) / 5));
}

function buildStage(w, roadIndex, stage, state) {
  const hazard = HAZARD_CYCLE[(stage + roadIndex) % HAZARD_CYCLE.length];
  const nextOffset =
    (stage % 2 === 0 ? 1 : -1) * (1.65 + Math.min(0.75, roadIndex * 0.08));
  const hardSurface = roadIndex >= 6 && stage % 3 === 1;
  const full = hardSurface ? "iiiii" : "nnnnn";
  const triple = hardSurface ? ".iii." : ".nnn.";
  const split = hardSurface ? "ii.ii" : "nn.nn";
  const launch = hardSurface ? "ib.bi" : "bb.bb";

  switch (stage % 6) {
    case 0:
      w.curve(full, 12, state.offset, nextOffset, {
        _elevation: state.height,
      })
        .hazard(full, 3, hazard, stage % 5, {
          _offset: nextOffset,
          _elevation: state.height,
        })
        .add(hardSurface ? "ididi" : "ndndn", 3, {
          _offset: nextOffset,
          _elevation: state.height,
        });
      break;
    case 1:
      w.add(split, 4, {
        _offset: state.offset,
        _elevation: state.height,
      })
        .add(launch, 1, {
          _offset: state.offset,
          _elevation: state.height,
        })
        .gap(gapFor(roadIndex, stage), {
          _offset: state.offset,
          _elevation: state.height,
        })
        .add(split, 4, {
          _offset: nextOffset,
          _elevation: state.height,
        })
        .hazard(split, 3, hazard, 1 + (stage % 3), {
          _offset: nextOffset,
          _elevation: state.height,
        });
      break;
    case 2: {
      const nextHeight = Math.max(
        0,
        state.height + (stage % 4 === 0 ? -1.15 : 1.45),
      );
      w.bendAndRamp("n.n.n", 14, state.offset, nextOffset, state.height, nextHeight)
        .add("c.c.c", 3, {
          _offset: nextOffset,
          _elevation: nextHeight,
        })
        .hazard("n.n.n", 3, hazard, 2, {
          _offset: nextOffset,
          _elevation: nextHeight,
        });
      state.height = nextHeight;
      break;
    }
    case 3:
      w.add("..n..", 5, {
        _offset: state.offset,
        _elevation: state.height,
      })
        .hazard("..n..", 3, hazard, 2, {
          _offset: state.offset,
          _elevation: state.height,
        })
        .jump(roadIndex >= 7 ? "..n.." : triple, gapFor(roadIndex, stage), {
          _offset: nextOffset,
          _elevation: state.height,
        }, "..b..")
        .setpiece("gauntlet-forge", stage % 2 ? -1 : 1, {
          intensity: roadIndex + 1,
        });
      break;
    case 4:
      w.curve("n...n", 12, state.offset, nextOffset, {
        _elevation: state.height,
      })
        .hazard("n...n", 3, hazard, stage % 2 ? 0 : 4, {
          _offset: nextOffset,
          _elevation: state.height,
        })
        .add("b...b", 1, {
          _offset: nextOffset,
          _elevation: state.height,
        })
        .gap(gapFor(roadIndex, stage), {
          _offset: nextOffset,
          _elevation: state.height,
        })
        .add("n...n", 4, {
          _offset: nextOffset,
          _elevation: state.height,
        });
      break;
    default: {
      const nextHeight = Math.max(0, state.height - 0.8 + (roadIndex % 2) * 1.5);
      w.bendAndRamp(triple, 13, state.offset, nextOffset, state.height, nextHeight)
        .hazard(triple, 3, hazard, 1 + (stage % 3), {
          _offset: nextOffset,
          _elevation: nextHeight,
        })
        .add(roadIndex >= 5 ? ".cuc." : ".uuu.", 2, {
          _offset: nextOffset,
          _elevation: nextHeight,
        });
      state.height = nextHeight;
      break;
    }
  }
  state.offset = nextOffset;
}

export function buildGauntletRoad(roadId, roadIndex) {
  const w = new RoadWriter(roadId, "boss");
  const state = { offset: 0, height: 0 };
  const stages = 9 + roadIndex;
  w.add("nnnnn", 8)
    .setpiece("gauntlet-gate", -1, { intensity: roadIndex + 1 })
    .add("bbbbb", 1);

  for (let stage = 0; stage < stages; stage += 1) {
    buildStage(w, roadIndex, stage, state);
  }

  w.bendAndRamp(
    roadIndex >= 7 ? ".nnn." : "nnnnn",
    15,
    state.offset,
    0,
    state.height,
    0,
  )
    .hazard(roadIndex >= 8 ? ".c.c." : "ndndn", 3, "arc", 2, {})
    .hazard(roadIndex >= 9 ? "..n.." : ".nnn.", 3, "meteor", 2, {})
    .jump(
      roadIndex >= 9 ? "..n.." : ".nnn.",
      Math.min(3, 2 + Math.floor(roadIndex / 5)),
    )
    .add("bbbbb", 1)
    .finish();

  return { rows: w.rows, desc: DESCRIPTIONS[roadIndex] };
}

